import { escape, get, last } from 'lodash';

import { defaultChartTitle } from '../utils/commonChartOptions';

export default function chartMixinFactory(chartType) {
  return {
    props: {
      groupBy: {
        type: Object,
        required: true
      },
      showLoader: {
        type: Boolean,
        default: false
      },
      selectedParameters: {
        type: Array,
        default() {
          return [];
        }
      },
      chartDrilldownPath: {
        type: Array,
        default() {
          return [];
        }
      },
      data: {
        type: Array,
        default() {
          return [];
        }
      },
      drilleddownData: {
        type: Object,
        default() {
          return {};
        }
      },
    },
    data() {
      return {
        drilleddownTo: [],
        chartDrilleddownTo: -1,
        drilldownOnClickXAxisLabelEventToCall: 0,
        drilldownXAxisLabelEventCalled: 0
      };
    },
    computed: {
      currentDrilldownItem() {
        return this.drilleddownTo[this.drilleddownTo.length - 1];
      },
      currentDrilldownLevel() {
        const currentDrilldownType =
          (this.currentDrilldownItem &&
            this.currentDrilldownItem.drilldownToType.field) ||
          this.groupBy.field;
        return this.chartDrilldownPath.findIndex(p => p.field === currentDrilldownType);
      },
      enableDrilldown() {
        return this.currentDrilldownLevel < this.chartDrilldownPath.length - 1;
      }
    },
    watch: {
      showLoader(newValue) {
        const chart = this.$refs['highchart'].chart;

        if (newValue && chart) {
          chart.showLoading(`Loading data...`);
        } else if (chart) {
          chart.hideLoading();
        }
      },
      chartDrilldownPath(newValue, oldValue) {
        if (!oldValue || !oldValue.length) {
          return;
        }

        const chart = this.$refs['highchart'] && this.$refs['highchart'].chart;
        const lastDrilldownLevel = last(chart.drilldownLevels);
        const currentLevel = get(lastDrilldownLevel, 'levelNumber', -1);

        if (currentLevel < 0) {
          return;
        }

        // Reset drilldown state if breakdown has been removed
        if (newValue.length < oldValue.length) {
          this.resetDrillDownState(currentLevel, newValue.length);
          return;
        }

        // Reset drilldown state if breakdown has been changed
        for (const index of newValue.keys()) {
          if (oldValue[index] !== newValue[index] && index <= currentLevel) {
            this.resetDrillDownState(currentLevel);
            break;
          }
        }
      }
    },
    methods: {
      resetDrillDownState(currentLevel) {
        const chart = this.$refs['highchart'] && this.$refs['highchart'].chart;

        for (let index = 0; index <= currentLevel; index++) {
          chart.drillUp();
        }
      },
      onDrilldown(e) {
        if (!this.enableDrilldown) {
          return;
        }

        if ((e.category !== undefined && this.drilldownXAxisLabelEventCalled > 0)) {
          this.drilldownXAxisLabelEventCalled++;

          if (this.drilldownOnClickXAxisLabelEventToCall === this.drilldownXAxisLabelEventCalled) {
            this.resetDrilldownEventCalledCountOnXAxisLabel();
          }

          return;
        }

        // When user clicks on the x-axis label of the column chart
        // the drilldown callback is called `no of series` times
        // but we need to handle it only once rather than multiple times
        if (e.category !== undefined) {
          this.drilldownOnClickXAxisLabelEventToCall = e.points.length;
          this.drilldownXAxisLabelEventCalled = 1;
        }

        const chart = e.target;
        const lastDrilldownType = get(this.currentDrilldownItem, 'drilldownToType.field', this.groupBy.field);
        const drilldownToType = this.chartDrilldownPath[this.currentDrilldownLevel + 1];

        // TODO: Change the title text to be more clear about what the drilldowned data indicates
        chart.setTitle({
          text: `${e.point.name}'s ${escape(drilldownToType.displayName)} data`
        });

        this.drilleddownTo.push({
          parentEntityName: e.point.id,
          parentEntityId: e.point.id,
          parentEntityType: lastDrilldownType,
          drilldownToType: drilldownToType
        });

        this.$emit('onDrilldown', {
          drilldowns: this.drilleddownTo,
          aggregateBy: drilldownToType.field,
          chartPoint: e.point,
          chartType: chartType,
          initialGroupBy: this.groupBy.field
        });

        // Note: Loader is shown/hidden according to the state on Vuex

        this.onDrilldownStateChange();
      },
      onDrillup(e) {
        try {
          const chart = e.target;
          let title = '';

          this.drilleddownTo.pop();
          const lastDrilldown = this.drilleddownTo[this.drilleddownTo.length - 1];

          // TODO: Change the title text to be more clear about what the drilldowned data indicates
          title =
            this.drilleddownTo.length && lastDrilldown
              ? `${escape(lastDrilldown.parentEntityName)}'s ${escape(lastDrilldown.drilldownToType.displayName)} data`
              : this.defaultTitle;

          title = title || defaultChartTitle;

          chart.setTitle({ text: title });

          this.onDrilldownStateChange();
          
          this.chartDrilleddownTo--;
        } catch {
          //
        }
      },
      onDrilldownStateChange() {
        this.$emit('onChartDrilldownChange', { drilleddownTo: this.drilleddownTo, chartType: chartType });
      },
      resetDrilldownEventCalledCountOnXAxisLabel() {
        this.drilldownOnClickXAxisLabelEventToCall = 0;
        this.drilldownXAxisLabelEventCalled = 0;
      }
    }
  };
}
